import { formatTimestamp } from "~/utils/helpers";
import { getNoteColorConfig } from "~/utils/components";
import { useRect } from "~/hooks/useRect";
import { useLabels } from "~/hooks/useLabels";
import { DEFAULT_NOTE_COLOR } from "~/constants";
import { useBreakpoints } from "~/hooks/useBreakpoints";

import { Box } from "../box/Box";
import { ColorPicker } from "../colorPicker/ColorPicker";
import { Popover } from "../popover/Popover";
import { useSheetModal } from "../sheetModal/SheetModal";
import { SheetModalGrabber } from "../sheetModal/SheetModalGrabber";

import type { ColorPickerColor } from "../colorPicker/ColorPicker";
import type { NoteColor } from "../noteCard/NoteCard";
import type { CSSProperties, PointerEvent } from "react";

export interface NoteEditorModalHeaderProps {
  color?: NoteColor;
  timestamp?: number;
  title?: string;
  onCancel?: () => void;
  onColorChange?: (value: ColorPickerColor) => void;
}

// Width at which to not try to full width the color picker.
const FULL_WIDTH_THRESHOLD = 400;

const PIE_SVG = `<svg fill="none" height="21" viewBox="0 0 20 21" width="20" xmlns="http://www.w3.org/2000/svg"><g stroke-width="1.5"><path d="m10 10.5v-9c3.2154 0 6.1865 1.71539 7.7942 4.5z" fill="#e8c281"/><path d="m10 10.5 7.7942-4.5c1.6077 2.78461 1.6077 6.2154 0 9z" fill="#f1b8b5"/><path d="m10 10.5 7.7942 4.5c-1.6077 2.7846-4.5788 4.5-7.7942 4.5z" fill="#c4c3e8"/><path d="m9.99735 10.5v9c-3.21539 0-6.18653-1.7154-7.79423-4.5z" fill="#91d1ea"/><path d="m10 10.5-7.79423 4.5c-1.607694-2.7846-1.607694-6.21539 0-9z" fill="#97d69c"/><path d="m9.99735 10.5-7.79423-4.5c1.6077-2.78461 4.57884-4.5 7.79423-4.5z" fill="#e4e8ed"/></g></svg>`;

const DEFAULT_LABELS = {
  "common.cta.cancel": "Cancel",
  "textEditor.selectColor": "Select Note Color",
};

export function NoteEditorModalHeader({
  color,
  timestamp,
  title,
  onCancel,
  onColorChange,
}: NoteEditorModalHeaderProps) {
  const { t } = useLabels({ fallback: DEFAULT_LABELS });

  const [ref, { width }] = useRect<HTMLDivElement>();

  const breakpoints = useBreakpoints();

  const { dragControls } = useSheetModal();

  const { metadata, metadataActive, background, text } = getNoteColorConfig(
    color ?? DEFAULT_NOTE_COLOR,
  );

  const style = {
    "--note-editor-modal-header-background": background,
    "--note-editor-modal-header-text": text,
    "--note-editor-modal-header-button": metadata,
    "--note-editor-modal-header-button-active": metadataActive,
    touchAction: "none",
  } as CSSProperties;

  const handlePointerDown = (e: PointerEvent) => {
    dragControls?.start(e);
  };

  return (
    <div
      ref={ref}
      className="NoteEditorModalHeader"
      style={style}
      onPointerDown={handlePointerDown}
    >
      {!breakpoints.lg && (
        <div className="NoteEditorModalHeader__grabber">
          <SheetModalGrabber />
        </div>
      )}

      <Box gap="var(--size-1)" align={"center"}>
        {title}

        <Box
          align={"center"}
          gap="var(--size-1)"
          style={{ marginInlineEnd: "auto" }}
        >
          {timestamp != null && (
            <>
              {"•"}
              <div className="NoteEditorModalHeader__timestamp">
                {formatTimestamp(timestamp)}
              </div>
            </>
          )}
        </Box>
      </Box>

      <Box gap="var(--size-1)" justify="end">
        <Popover
          fullWidth={width < FULL_WIDTH_THRESHOLD}
          offset={{ crossAxis: 100 }}
        >
          <Popover.Trigger>
            <button
              aria-label={t("textEditor.selectColor")}
              className="NoteEditorModalHeader__button NoteEditorModalHeader__button--circle"
              dangerouslySetInnerHTML={{ __html: PIE_SVG }}
              onClick={(e) => e.preventDefault()}
            />
          </Popover.Trigger>

          <Popover.Panel>
            <ColorPicker
              value={color}
              onChange={({ value }) => {
                onColorChange?.(value);
              }}
            />
          </Popover.Panel>
        </Popover>

        <button
          style={{ gap: "var(--size-0-5)" }}
          aria-label={t("common.cta.cancel")}
          className="NoteEditorModalHeader__button"
          onClick={onCancel}
        >
          {t("common.cta.cancel")}
        </button>
      </Box>
    </div>
  );
}
